import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Globe } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface CountryOption {
  value: string;
  label: string;
  flag?: string;
}

interface CountryComboboxProps {
  options?: CountryOption[];
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  className?: string;
  triggerClassName?: string;
  showIcon?: boolean;
  optionTestIdPrefix?: string;
  id?: string;
  "data-testid"?: string;
}

export function CountryCombobox({
  id,
  options: optionsProp,
  value,
  onValueChange,
  placeholder = "Select country...",
  searchPlaceholder = "Search countries...",
  emptyText = "No country found.",
  className,
  triggerClassName,
  showIcon = false,
  optionTestIdPrefix = "option-country",
  "data-testid": testId,
}: CountryComboboxProps) {
  const { data: countriesData } = useQuery<Array<{ code: string; name: string; flag: string }>>({
    queryKey: ["/api/countries"],
    enabled: !optionsProp,
  });

  const options = (
    optionsProp ?? countriesData?.map(c => ({ value: c.code, label: c.name, flag: c.flag })) ?? []
  )
    .slice()
    .sort((a, b) => a.label.localeCompare(b.label));
  const [open, setOpen] = useState(false);

  const selected = options.find((opt) => opt.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
            triggerClassName
          )}
          data-testid={testId}
        >
          <span className="flex items-center gap-2 truncate">
            {showIcon && !selected?.flag && (
              <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
            {selected ? (
              <>
                {selected.flag && <span className="shrink-0">{selected.flag}</span>}
                <span className="truncate">{selected.label}</span>
              </>
            ) : (
              <span className="truncate">{placeholder}</span>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          "p-0 w-[--radix-popover-trigger-width] min-w-[16rem]",
          className
        )}
        align="start"
      >
        <Command loop>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList className="max-h-[300px] overflow-y-auto overscroll-contain">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={`${option.label} ${option.value}`}
                  onSelect={() => {
                    onValueChange(option.value);
                    setOpen(false);
                  }}
                  data-testid={`${optionTestIdPrefix}-${option.value}`}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4 shrink-0",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option.flag && <span className="shrink-0">{option.flag}</span>}
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
